import { Button } from "@/components/ui/button";
import {
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import VehicleSelect from "@/components/ui/vehicle-select";
import { ChevronRight, Plus, Trash2 } from "lucide-react";
import { useState } from "react";
import { type UseFormReturn, useFieldArray } from "react-hook-form";
import { toast } from "sonner";
import type { GeofenceFormValues } from "./form";

interface GeofenceActionsProps {
  form: UseFormReturn<GeofenceFormValues>;
}

const GeofenceActions = ({ form }: GeofenceActionsProps) => {
  const [openGroups, setOpenGroups] = useState<Record<number, boolean>>({});
  const { fields, append, remove, update } = useFieldArray({
    control: form.control,
    name: "actions",
  });
  const actions = form.watch("actions") ?? [];

  const handleAddAction = () => {
    const vehicleIds = form.getValues("tempVehicleIds") ?? [];
    const vehicles = form.getValues("tempVehicles") ?? [];
    const entryExit = form.getValues("tempEntryExit") ?? 0;
    const action = form.getValues("tempAction") ?? 2;
    const log = form.getValues("tempLog") ?? "";

    if (vehicleIds.length === 0) {
      toast.error("Selecione ao menos um veículo");
      return;
    }

    if (action === 1 && !log) {
      toast.error("Digite um email para receber a notificação");
      return;
    }

    append({
      vehicle_ids: vehicleIds,
      vehicles,
      action,
      entry_exit: entryExit,
      log,
    });
    setOpenGroups((current) => ({ ...current, [entryExit]: true }));
    form.setValue("tempVehicleIds", []);
    form.setValue("tempVehicles", []);
    form.setValue("tempEntryExit", 0);
    form.setValue("tempAction", 2);
    form.setValue("tempLog", "");
    toast.success("Ação adicionada com sucesso");
  };

  const handleRemoveVehicle = (actionIndex: number, vehicleId: number) => {
    const currentAction = actions[actionIndex];

    if (currentAction.vehicle_ids.length === 1) {
      remove(actionIndex);
      return;
    }

    update(actionIndex, {
      ...currentAction,
      vehicle_ids: currentAction.vehicle_ids.filter((id) => id !== vehicleId),
      vehicles: currentAction.vehicles?.filter(
        (vehicle) => vehicle.id !== vehicleId,
      ),
    });
  };

  const getActionText = (action: (typeof actions)[number]) =>
    action.action === 1
      ? `Enviar Email${action.log ? ` para ${action.log}` : ""}`
      : "Enviar notificação na plataforma web/mobile";

  const renderActionGroup = (entryExit: number, title: string) => {
    const isOpen = openGroups[entryExit] === true;
    const groupActions = actions
      .map((action, index) => ({ action, index }))
      .filter(({ action }) => action.entry_exit === entryExit);

    return (
      <div className="border-b last:border-b-0">
        <button
          type="button"
          aria-expanded={isOpen}
          onClick={() =>
            setOpenGroups((current) => ({
              ...current,
              [entryExit]: !current[entryExit],
            }))
          }
          className="flex w-full items-center gap-2 px-3 py-3 text-left text-sm font-medium hover:bg-muted/50"
        >
          <ChevronRight
            className={`h-4 w-4 transition-transform ${isOpen ? "rotate-90" : ""}`}
          />
          {title}
        </button>

        {isOpen && (
          <div className="space-y-2 px-4 pb-3 pl-9">
            {groupActions.length === 0 ? (
              <p className="text-sm text-muted-foreground">
                Nenhuma ação configurada.
              </p>
            ) : (
              groupActions.flatMap(({ action, index }) =>
                action.vehicle_ids.map((vehicleId) => {
                  const vehicleLabel =
                    action.vehicles?.find((vehicle) => vehicle.id === vehicleId)
                      ?.label ?? `Veículo #${vehicleId}`;
                  const entryExitText =
                    entryExit === 1
                      ? "Entrar na cerca eletrônica"
                      : "Sair da cerca eletrônica";

                  return (
                    <div
                      key={`${fields[index]?.id}-${vehicleId}`}
                      className="flex items-start gap-2 text-sm"
                    >
                      <span className="mt-1">•</span>
                      <span className="flex-1">
                        Quando <b>{vehicleLabel}</b> {entryExitText}{" "}
                        {getActionText(action)}.
                      </span>
                      <Button
                        type="button"
                        variant="ghost"
                        size="icon"
                        aria-label={`Remover ação de ${vehicleLabel}`}
                        onClick={() => handleRemoveVehicle(index, vehicleId)}
                        className="h-7 w-7 shrink-0"
                      >
                        <Trash2 className="h-4 w-4 text-destructive" />
                      </Button>
                    </div>
                  );
                }),
              )
            )}
          </div>
        )}
      </div>
    );
  };

  return (
    <div>
      <h3 className="text-md mb-4 font-medium">Criar ação para esta área</h3>

      <div className="mb-4 rounded-md border p-4">
        <div className="flex flex-col gap-4">
          <FormField
            control={form.control}
            name="tempVehicleIds"
            render={({ field }) => (
              <FormItem className="flex flex-col">
                <FormLabel>Veículos</FormLabel>
                <VehicleSelect
                  multiple
                  value={field.value ?? []}
                  onChange={field.onChange}
                  onSelectedVehiclesChange={(vehicles) =>
                    form.setValue(
                      "tempVehicles",
                      vehicles.map((vehicle) => ({
                        id: vehicle.id,
                        label: `${vehicle.plate} - ${vehicle.title}`,
                      })),
                    )
                  }
                />
                <FormMessage />
              </FormItem>
            )}
          />

          <FormField
            control={form.control}
            name="tempEntryExit"
            render={({ field }) => (
              <FormItem className="flex flex-col">
                <FormLabel>Quando?</FormLabel>
                <Select
                  value={String(field.value ?? 0)}
                  onValueChange={(value) => field.onChange(Number(value))}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Selecione" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="0">Sair da cerca eletrônica</SelectItem>
                    <SelectItem value="1">
                      Entrar na cerca eletrônica
                    </SelectItem>
                  </SelectContent>
                </Select>
                <FormMessage />
              </FormItem>
            )}
          />

          <FormField
            control={form.control}
            name="tempAction"
            render={({ field }) => (
              <FormItem className="flex flex-col">
                <FormLabel>Tomar ação</FormLabel>
                <Select
                  value={String(field.value ?? 2)}
                  onValueChange={(value) => field.onChange(Number(value))}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Selecione" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="1">Enviar Email</SelectItem>
                    <SelectItem value="2">
                      Enviar notificação na plataforma web/mobile
                    </SelectItem>
                  </SelectContent>
                </Select>
                <FormMessage />
              </FormItem>
            )}
          />

          {form.watch("tempAction") === 1 && (
            <FormField
              control={form.control}
              name="tempLog"
              render={({ field }) => (
                <FormItem className="flex flex-col">
                  <FormLabel>Para</FormLabel>
                  <Input
                    type="email"
                    placeholder="Digite o email"
                    value={field.value}
                    onChange={field.onChange}
                  />
                  <FormMessage />
                </FormItem>
              )}
            />
          )}
        </div>

        <Button
          type="button"
          variant="outline"
          onClick={handleAddAction}
          className="mt-4 w-full"
        >
          <Plus className="mr-2 h-4 w-4" />
          Adicionar ação
        </Button>
      </div>

      <div>
        <h3 className="text-md mb-2 font-medium">Ações configuradas</h3>
        <div className="overflow-hidden rounded-md border">
          {renderActionGroup(1, "Entrada de cerca")}
          {renderActionGroup(0, "Saída de cerca")}
        </div>
      </div>
    </div>
  );
};

export default GeofenceActions;
